import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { HireClippersOption } from '@/lib/brandLeads/hireClippersOptions';
import { cn } from '@/lib/utils';

interface HireClippersOptionGridProps {
  /** Step name; labels come from `hireClippers.options.<group>.<id>`. */
  group: string;
  options: readonly HireClippersOption[];
  selected: readonly string[];
  disabled?: boolean;
  onToggle: (value: string) => void;
}

export function HireClippersOptionGrid({
  group,
  options,
  selected,
  disabled = false,
  onToggle,
}: HireClippersOptionGridProps) {
  const { t } = useTranslation();

  return (
    <div className="grid gap-2.5 sm:grid-cols-2">
      {options.map((option) => {
        const isSelected = selected.includes(option.value);
        return (
          <button
            key={option.id}
            type="button"
            aria-pressed={isSelected}
            disabled={disabled}
            data-testid={`hire-clippers-option-${option.id}`}
            onClick={() => onToggle(option.value)}
            className={cn(
              'flex min-h-12 items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left text-sm transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              'disabled:cursor-not-allowed disabled:opacity-60',
              isSelected
                ? 'border-primary bg-primary/10 text-foreground'
                : 'border-border bg-card hover:border-primary/40 hover:bg-secondary',
            )}
          >
            <span>{t(`hireClippers.options.${group}.${option.id}`)}</span>
            {isSelected && (
              <Check aria-hidden="true" className="h-4 w-4 shrink-0 text-primary" />
            )}
          </button>
        );
      })}
    </div>
  );
}
